import React, { useCallback, useState, useContext } from "react";
import { IImageLoaderChildrenProps, ImageLoader } from "@montana/shared/image-loader";
import { CrawlerContext } from "../../context/crawler.context";
import { convertMediaUrl } from "@montana/helpers/media-src.helper";
import { InView } from "../in-view";
import { IImage } from "@montana/models/generated/cms.interface";

interface IBackgroundImageProps {
    imageReference: IImage | undefined;
    appearAnimation?: boolean;
}


export const BackgroundImage: React.FC<IBackgroundImageProps> = (props: IBackgroundImageProps): JSX.Element | null => {

    const [boundDOMNode, setBoundDOMNode] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [imageError, setImageError] = useState(false);
    const domRef = useCallback((node): false | void => boundDOMNode !== node && setBoundDOMNode(node), []);

    // If the "client" is a crawler, we'll just cheat and imagine the image is already loaded. The SSR renderer can
    // tell us these things. The CSR renderer is too cool to care.
    const isCrawler = useContext(CrawlerContext);
    const [imageReady, setImageReady] = useState(isCrawler);

    if (!props || !props.imageReference) {
        return null;
    }

    const style = imageReady
        ? { backgroundImage: `url('${convertMediaUrl(props.imageReference.url)}')` }
        : {};

    const callbackFunction = (childProps: IImageLoaderChildrenProps): void => {
        const { loaded, error, loading } = childProps;

        setImageReady(loaded);
        setImageError(error);
        setImageLoading(loading);
    };

    // eslint-disable-next-line no-nested-ternary
    const modifier = imageReady
        ? 'loaded'
        // eslint-disable-next-line no-nested-ternary
        : imageLoading
            ? 'loading'
            : imageError
                ? 'error'
                : 'idle';


    return (
        props.appearAnimation ? (
            <InView singleFire>
                {({ inView, ref }): JSX.Element => (
                    <aside ref={domRef} className={`background-image background-image--${modifier} ${inView ? "background-image--in-view" : ""} `} style={style}>
                        <div className="absolute inset-0" ref={ref}>
                            <ImageLoader imageReference={props.imageReference as IImage} domReference={boundDOMNode}>{callbackFunction}</ImageLoader>
                        </div>
                    </aside>
                )}
            </InView>
        ) : (
            <aside ref={domRef} className={`background-image background-image--${modifier}`} style={style}>
                <ImageLoader imageReference={props.imageReference} domReference={boundDOMNode}>{callbackFunction}</ImageLoader>
            </aside>
        )
    );
};

BackgroundImage.defaultProps = {
    appearAnimation: false,
}
